import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Play } from "lucide-react";
import { HERO_IMAGE } from "../lib/data";
import { useContent } from "../context/ContentContext";

export const Hero = () => {
  const ref = useRef(null);
  const { content } = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const hero = content?.hero || {};
  const stats = content?.stats || [];

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#050505] grain"
    >
      <motion.div style={{ y: yImg }} className="absolute inset-0 will-change-transform" aria-hidden>
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <div className="absolute -bottom-32 -right-32 w-[42rem] h-[42rem] rounded-full bg-[#ff0033]/20 blur-[140px]" />
        <div className="absolute top-1/3 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#ff0033]/10 blur-[120px]" />
      </motion.div>

      <div className="absolute top-20 left-0 right-0 h-px hairline opacity-40" />
      <div className="absolute bottom-0 left-0 right-0 h-px hairline opacity-40" />

      <div className="absolute top-24 left-0 right-0 z-20 max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between text-[10px] tracking-[0.4em] uppercase text-zinc-500">
        <div className="flex items-center gap-3">
          <span className="red-led" />
          <span data-testid="hero-meta-left">EST. 2025 · DE / EU</span>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <span>SCROLL</span>
          <ArrowDownRight size={14} className="text-[#ff0033]" />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-44 pb-32 md:pb-24 min-h-[100vh] flex flex-col lg:flex-row gap-10 md:gap-16 items-end">
        <motion.div style={{ y: yText, opacity }} className="flex-1 max-w-2xl">
          <div className="text-[11px] tracking-[0.45em] uppercase text-zinc-500 font-semibold mb-8 flex items-center gap-3">
            <span className="block w-10 h-px bg-[#ff0033] shadow-[0_0_10px_#ff0033]" />
            {hero.eyebrow || "Independent Music Label"}
          </div>

          <h1
            data-testid="hero-title"
            className="font-display font-black uppercase leading-[0.85] text-[11vw] sm:text-[12vw] lg:text-[8.5vw] tracking-tighter"
          >
            <span className="block text-white text-glow-red-strong">{hero.line1 || "STRAßENWERT."}</span>
            <span className="block text-zinc-500/90 text-[6vw] sm:text-[7vw] lg:text-[5.5vw] mt-3 text-glow-red">
              {hero.line2 || "Musik mit Straßenwert."}
            </span>
          </h1>

          <p
            data-testid="hero-tagline"
            className="mt-8 md:mt-10 max-w-xl text-zinc-400 text-sm md:text-lg leading-relaxed font-medium"
          >
            {hero.tagline}
          </p>

          <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-3 md:gap-4">
            <button
              data-testid="hero-cta-primary"
              onClick={() => goTo("kontakt")}
              className="group relative inline-flex items-center gap-3 bg-white text-black px-5 md:px-7 py-3.5 md:py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden hover:shadow-[0_0_24px_rgba(255,255,255,0.25)] transition-all"
            >
              <span className="relative z-10">{hero.cta_primary || "Jetzt bewerben"}</span>
              <ArrowDownRight size={14} className="relative z-10 group-hover:rotate-[-45deg] transition-transform duration-500" />
              <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => goTo("leistungen")}
              className="inline-flex items-center gap-3 border border-white/20 px-5 md:px-7 py-3.5 md:py-4 text-[10px] md:text-[11px] font-bold tracking-[0.3em] uppercase text-zinc-200 hover:border-[#ff0033]/60 hover:text-white hover:shadow-[0_0_20px_rgba(255,0,51,0.2)] transition-all"
            >
              <Play size={12} className="text-[#ff0033]" /> {hero.cta_secondary || "Unsere Leistungen"}
            </button>
          </div>
        </motion.div>

        <div data-testid="hero-stats" className="w-full lg:w-[420px] grid grid-cols-2 gap-3">
          {stats.map((s, i) => (
            <motion.div
              key={s.label + i}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + i * 0.12, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
              className="glass glass-hover relative p-4 md:p-5 rounded-sm group"
              data-testid={`hero-stat-${i}`}
            >
              <div className="absolute top-3 right-3 red-led opacity-60" />
              <div className="text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-semibold mb-2 md:mb-3">
                {s.label}
              </div>
              <div className="font-display font-black text-3xl md:text-4xl text-white leading-none">{s.value}</div>
              <div className="text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-2 md:mt-3">{s.note}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/[0.06] bg-[#050505]/60 backdrop-blur-md overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0 items-center gap-10 pr-10">
              {["Management", "Promotion", "Strategie", "Content", "Studios", "A&R", "Branding", "Distribution", "Touring"].map((w) => (
                <span key={w + k} className="flex items-center gap-10">
                  <span>{w}</span>
                  <span className="text-[#ff0033]">/</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
